// ═══════════════════════════════════════════════════════════════════════════════
// components/AdsManagerPanel.jsx — Panel Ads Manager
// WeConnect CRM — Módulo Ads Manager Frontend
//
// Dashboard + Wizard creación + Biblioteca creativos + Detalle campaña
// ARCHIVO NUEVO — No modifica nada existente
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../constants';

// ─── API Helper ─────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('wc_token') || '';
  return {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  };
}

async function adsApi(endpoint, options = {}) {
  const url = API_URL + '/api/ads' + endpoint;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
    ...options
  });
  const data = await res.json();
  if (!data.ok && data.error) throw new Error(data.error);
  return data;
}

async function adsUpload(file, nombre) {
  const token = localStorage.getItem('wc_token') || '';
  const form = new FormData();
  form.append('imagen', file);
  form.append('nombre', nombre || file.name);
  const res = await fetch(API_URL + '/api/ads/creativos/subir', {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + token },
    body: form
  });
  const data = await res.json();
  if (!data.ok && data.error) throw new Error(data.error);
  return data;
}

// ─── Estilos ────────────────────────────────────────────────────────────────

const S = {
  panel: {
    padding: '20px', color: '#e2e0d8', minHeight: '100%',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    gridColumn: '2 / -1', overflowY: 'auto', height: 'calc(100vh - 56px)'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', flexWrap: 'wrap', gap: '10px'
  },
  title: { fontSize: '20px', fontWeight: '700', margin: 0 },
  subtitle: { fontSize: '13px', color: '#8a8880', marginTop: '2px' },
  btnPrimary: {
    background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'
  },
  btnSecondary: {
    background: '#2a2a35', color: '#e2e0d8', border: '1px solid #3a3a45',
    borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px'
  },
  btnDanger: {
    background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '8px 16px', cursor: 'pointer', fontSize: '13px'
  },
  btnSuccess: {
    background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'
  },
  card: {
    background: '#1a1a24', border: '1px solid #2a2a35', borderRadius: '12px',
    padding: '16px', marginBottom: '12px'
  },
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px', marginBottom: '20px'
  },
  kpi: {
    background: '#1a1a24', border: '1px solid #2a2a35', borderRadius: '12px',
    padding: '16px', textAlign: 'center'
  },
  kpiValue: { fontSize: '24px', fontWeight: '700', color: '#fff' },
  kpiLabel: { fontSize: '12px', color: '#8a8880', marginTop: '4px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: '#8a8880',
    borderBottom: '1px solid #2a2a35', fontWeight: '600', textTransform: 'uppercase'
  },
  td: {
    padding: '12px', fontSize: '14px', borderBottom: '1px solid #1e1e28',
    verticalAlign: 'middle'
  },
  badge: (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
    fontSize: '11px', fontWeight: '600',
    background: color + '22', color: color
  }),
  input: {
    background: '#12121a', border: '1px solid #2a2a35', borderRadius: '8px',
    padding: '10px 14px', color: '#e2e0d8', width: '100%', fontSize: '14px',
    boxSizing: 'border-box', outline: 'none'
  },
  select: {
    background: '#12121a', border: '1px solid #2a2a35', borderRadius: '8px',
    padding: '10px 14px', color: '#e2e0d8', width: '100%', fontSize: '14px',
    boxSizing: 'border-box'
  },
  textarea: {
    background: '#12121a', border: '1px solid #2a2a35', borderRadius: '8px',
    padding: '10px 14px', color: '#e2e0d8', width: '100%', fontSize: '14px',
    boxSizing: 'border-box', resize: 'vertical', minHeight: '80px', outline: 'none'
  },
  label: { display: 'block', fontSize: '13px', color: '#8a8880', marginBottom: '6px', fontWeight: '600' },
  fieldGroup: { marginBottom: '16px' },
  steps: {
    display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap'
  },
  step: (active, done) => ({
    padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600',
    background: active ? '#3b82f6' : done ? '#16a34a33' : '#1a1a24',
    color: active ? '#fff' : done ? '#16a34a' : '#8a8880',
    border: '1px solid ' + (active ? '#3b82f6' : done ? '#16a34a44' : '#2a2a35')
  }),
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px'
  },
  imgCard: (selected) => ({
    background: '#12121a', border: '2px solid ' + (selected ? '#3b82f6' : '#2a2a35'),
    borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', position: 'relative'
  }),
  imgThumb: { width: '100%', height: '120px', objectFit: 'cover', display: 'block' },
  imgInfo: { padding: '8px', fontSize: '11px', color: '#8a8880' },
  charCount: (current, max) => ({
    fontSize: '11px', textAlign: 'right', marginTop: '4px',
    color: current > max ? '#dc2626' : '#8a8880'
  }),
  row: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  col: { flex: 1, minWidth: '200px' },
  divider: { borderTop: '1px solid #2a2a35', margin: '20px 0' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#8a8880' },
  link: { color: '#3b82f6', cursor: 'pointer', textDecoration: 'none' },
  alert: (type) => ({
    padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
    background: type === 'error' ? '#dc262622' : type === 'success' ? '#16a34a22' : '#3b82f622',
    color: type === 'error' ? '#f87171' : type === 'success' ? '#4ade80' : '#60a5fa',
    border: '1px solid ' + (type === 'error' ? '#dc262644' : type === 'success' ? '#16a34a44' : '#3b82f644')
  }),
  bar: (pct, color) => ({
    height: '6px', borderRadius: '3px', background: '#2a2a35', overflow: 'hidden', marginTop: '8px',
    position: 'relative'
  })
};

// ─── Constantes ─────────────────────────────────────────────────────────────

const ESTADO_COLORS = {
  borrador: '#6b7280', activa: '#16a34a', pausada: '#eab308',
  finalizada: '#6b7280', error: '#dc2626'
};

const TIPO_LABELS = { ctwa: 'Click-to-WhatsApp', trafico_landing: 'Tráfico a Landing' };

const ZONA_LABELS = {
  brown: 'Almirante Brown (8km)', varela: 'Florencio Varela (8km)',
  sarmiento: 'Capitán Sarmiento (5km)', todas: 'Todas las zonas'
};

const formatMoney = (v) => {
  if (!v && v !== 0) return '$0';
  return '$' + Number(v).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatNumber = (v) => {
  if (!v && v !== 0) return '0';
  return Number(v).toLocaleString('es-AR');
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function AdsManagerPanel() {
  const [vista, setVista] = useState('dashboard'); // dashboard, wizard, detail, library
  const [campanas, setCampanas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [creativos, setCreativos] = useState([]);
  const [campanaSeleccionada, setCampanaSeleccionada] = useState(null);
  const [metricas, setMetricas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    nombre: '', tipo: 'ctwa', presupuesto_diario: '',
    fecha_inicio: '', fecha_fin: '', zona: 'brown',
    edad_min: 25, edad_max: 55, genero: 'todos',
    creativo_id: null, texto_principal: '', titulo: '', descripcion: ''
  });

  // ─── Cargar datos ───────────────────────────────────────────────────────

  const cargarResumen = useCallback(async () => {
    try {
      const data = await adsApi('/resumen');
      setResumen(data.resumen);
      setCampanas(data.resumen.campanas || []);
    } catch (e) { console.error('[ADS-UI]', e.message); }
  }, []);

  const cargarCampanas = useCallback(async () => {
    try {
      const data = await adsApi('/campanas');
      setCampanas(data.campanas || []);
    } catch (e) { console.error('[ADS-UI]', e.message); }
  }, []);

  const cargarCreativos = useCallback(async () => {
    try {
      const data = await adsApi('/creativos');
      setCreativos(data.creativos || []);
    } catch (e) { console.error('[ADS-UI]', e.message); }
  }, []);

  useEffect(() => {
    cargarResumen();
    cargarCreativos();
  }, [cargarResumen, cargarCreativos]);

  // Limpiar mensajes
  useEffect(() => {
    if (msg) { const t = setTimeout(() => setMsg(null), 5000); return () => clearTimeout(t); }
  }, [msg]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(null), 8000); return () => clearTimeout(t); }
  }, [error]);

  // ─── Acciones ───────────────────────────────────────────────────────────

  const crearBorrador = async () => {
    setLoading(true); setError(null);
    try {
      const data = await adsApi('/campanas', {
        method: 'POST',
        body: JSON.stringify(wizardData)
      });
      setMsg('Borrador creado: ' + data.campana.nombre);
      setCampanaSeleccionada(data.campana);
      setVista('detail');
      cargarCampanas();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const publicarCampana = async (id) => {
    if (!window.confirm('¿Publicar esta campaña en Meta? Se creará con estado PAUSADA.')) return;
    setLoading(true); setError(null);
    try {
      const data = await adsApi('/campanas/' + id + '/publicar', { method: 'POST' });
      setMsg(data.mensaje || 'Campaña publicada en Meta');
      cargarDetalle(id);
      cargarResumen();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const pausarCampana = async (id) => {
    setLoading(true);
    try {
      await adsApi('/campanas/' + id + '/pausar', { method: 'POST' });
      setMsg('Campaña pausada'); cargarDetalle(id); cargarResumen();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const reanudarCampana = async (id) => {
    if (!window.confirm('¿Activar esta campaña? Empezará a gastar presupuesto real.')) return;
    setLoading(true);
    try {
      await adsApi('/campanas/' + id + '/reanudar', { method: 'POST' });
      setMsg('Campaña activada'); cargarDetalle(id); cargarResumen();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const eliminarCampana = async (id) => {
    if (!window.confirm('¿Eliminar/archivar esta campaña? No se puede deshacer.')) return;
    setLoading(true);
    try {
      await adsApi('/campanas/' + id + '/eliminar', { method: 'POST' });
      setMsg('Campaña archivada'); setVista('dashboard'); cargarResumen();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const syncCampana = async (id) => {
    setLoading(true);
    try {
      const data = await adsApi('/campanas/' + id + '/sync');
      setMsg('Métricas sincronizadas: ' + data.dias_sincronizados + ' días');
      cargarDetalle(id);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const syncTodasCampanas = async () => {
    if (!window.confirm('?Sincronizar m?tricas de todas las campa?as con Meta ahora?')) return;

    setLoading(true);
    setError(null);

    try {
      await adsApi('/sync');
      setMsg('M?tricas sincronizadas para todas las campa?as');
      await cargarResumen();
    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
  };

  const cargarDetalle = async (id) => {
    try {
      const data = await adsApi('/campanas/' + id);
      setCampanaSeleccionada(data.campana);
      const met = await adsApi('/campanas/' + id + '/metricas?dias=30');
      setMetricas(met.metricas || []);
    } catch (e) { setError(e.message); }
  };

  const subirCreativo = async (file) => {
    setLoading(true); setError(null);
    try {
      await adsUpload(file, file.name);
      setMsg('Creativo subido: ' + file.name);
      cargarCreativos();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const eliminarCreativo = async (id) => {
    if (!window.confirm('¿Eliminar este creativo?')) return;
    try {
      await adsApi('/creativos/' + id, { method: 'DELETE' });
      setMsg('Creativo eliminado');
      cargarCreativos();
    } catch (e) { setError(e.message); }
  };

  // ─── Iniciar wizard ────────────────────────────────────────────────────

  const iniciarWizard = () => {
    setWizardData({
      nombre: '', tipo: 'ctwa', presupuesto_diario: '',
      fecha_inicio: '', fecha_fin: '', zona: 'brown',
      edad_min: 25, edad_max: 55, genero: 'todos',
      creativo_id: null, texto_principal: '', titulo: '', descripcion: ''
    });
    setWizardStep(1);
    setVista('wizard');
    cargarCreativos();
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div style={S.panel}>
      {/* Alerts */}
      {error && <div style={S.alert('error')}>⚠ {error}</div>}
      {msg && <div style={S.alert('success')}>✓ {msg}</div>}

      {vista === 'dashboard' && renderDashboard()}
      {vista === 'wizard' && renderWizard()}
      {vista === 'detail' && renderDetail()}
      {vista === 'library' && renderLibrary()}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════

  function renderDashboard() {
    return (
      <>
        <div style={S.header}>
          <div>
            <h2 style={S.title}>📊 Ads Manager</h2>
            <div style={S.subtitle}>Campañas Meta — WeConnect CRM Ads</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={S.btnSecondary} onClick={() => { setVista('library'); cargarCreativos(); }}>
              🖼 Creativos
            </button>
            <button style={S.btnPrimary} onClick={iniciarWizard}>
              + Nueva campaña
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div style={S.kpiRow}>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatMoney(resumen?.gasto_mes)}</div>
            <div style={S.kpiLabel}>Gasto del mes</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatNumber(resumen?.leads_mes)}</div>
            <div style={S.kpiLabel}>Leads del mes</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatNumber(resumen?.conversaciones_mes)}</div>
            <div style={S.kpiLabel}>Conversaciones</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatMoney(resumen?.cpl_mes)}</div>
            <div style={S.kpiLabel}>Costo por lead</div>
          </div>
          <div style={S.kpi}>
            <div style={{ ...S.kpiValue, color: '#16a34a' }}>{resumen?.campanas_activas || 0}</div>
            <div style={S.kpiLabel}>Activas</div>
          </div>
        </div>

        {/* Lista campañas */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontWeight: '600', fontSize: '15px' }}>Campañas</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...S.btnSecondary, fontSize: '12px', padding: '6px 12px' }} onClick={cargarResumen} disabled={loading}>
                ? Actualizar
              </button>
              <button style={{ ...S.btnSecondary, fontSize: '12px', padding: '6px 12px', borderColor: '#3b82f6' }} onClick={syncTodasCampanas} disabled={loading}>
                {loading ? 'Sincronizando...' : '? Sync todas'}
              </button>
            </div>
          </div>

          {campanas.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>📢</div>
              <div>No hay campañas todavía</div>
              <div style={{ marginTop: '8px' }}>
                <span style={S.link} onClick={iniciarWizard}>Crear la primera campaña</span>
              </div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Estado</th>
                  <th style={S.th}>Campaña</th>
                  <th style={S.th}>Tipo</th>
                  <th style={S.th}>Gasto</th>
                  <th style={S.th}>Leads</th>
                  <th style={S.th}>CPL</th>
                  <th style={S.th}>CTR</th>
                </tr>
              </thead>
              <tbody>
                {campanas.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }}
                    onClick={() => { cargarDetalle(c.id); setVista('detail'); }}>
                    <td style={S.td}>
                      <span style={S.badge(ESTADO_COLORS[c.estado] || '#6b7280')}>
                        {c.estado}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontWeight: '600' }}>{c.nombre}</td>
                    <td style={S.td}>{c.tipo === 'ctwa' ? '💬 CTWA' : '🌐 Landing'}</td>
                    <td style={S.td}>{formatMoney(c.gasto_total)}</td>
                    <td style={S.td}>{c.tipo === 'ctwa' ? formatNumber(c.conversaciones) : formatNumber(c.leads)}</td>
                    <td style={S.td}>{formatMoney(c.cpl)}</td>
                    <td style={S.td}>{c.ctr ? c.ctr.toFixed(1) + '%' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WIZARD — 5 pasos
  // ═══════════════════════════════════════════════════════════════════════════

  function renderWizard() {
    const w = wizardData;
    const upd = (field, val) => setWizardData(prev => ({ ...prev, [field]: val }));

    return (
      <>
        <div style={S.header}>
          <div>
            <h2 style={S.title}>Nueva campaña</h2>
            <div style={S.subtitle}>Paso {wizardStep} de 5</div>
          </div>
          <button style={S.btnSecondary} onClick={() => setVista('dashboard')}>✕ Cancelar</button>
        </div>

        {/* Steps indicator */}
        <div style={S.steps}>
          {['Tipo', 'Público', 'Creativo', 'Textos', 'Confirmar'].map((label, i) => (
            <span key={i} style={S.step(wizardStep === i + 1, wizardStep > i + 1)}>
              {wizardStep > i + 1 ? '✓ ' : ''}{label}
            </span>
          ))}
        </div>

        <div style={S.card}>
          {wizardStep === 1 && (
            <>
              <div style={S.fieldGroup}>
                <label style={S.label}>Tipo de campaña</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[['ctwa', '💬 Click-to-WhatsApp'], ['trafico_landing', '🌐 Tráfico a Landing']].map(([val, lab]) => (
                    <button key={val}
                      style={{
                        ...S.btnSecondary, flex: 1, padding: '14px',
                        border: w.tipo === val ? '2px solid #3b82f6' : '1px solid #3a3a45',
                        background: w.tipo === val ? '#3b82f622' : '#2a2a35'
                      }}
                      onClick={() => upd('tipo', val)}>
                      {lab}
                    </button>
                  ))}
                </div>
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Nombre de la campaña</label>
                <input style={S.input} placeholder="Ej: Fibra Brown - Julio 2026"
                  value={w.nombre} onChange={e => upd('nombre', e.target.value)} />
              </div>
              <div style={S.row}>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Presupuesto diario (ARS)</label>
                    <input style={S.input} type="number" placeholder="Ej: 5000"
                      value={w.presupuesto_diario} onChange={e => upd('presupuesto_diario', e.target.value)} />
                  </div>
                </div>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Fecha inicio</label>
                    <input style={S.input} type="date"
                      value={w.fecha_inicio} onChange={e => upd('fecha_inicio', e.target.value)} />
                  </div>
                </div>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Fecha fin</label>
                    <input style={S.input} type="date"
                      value={w.fecha_fin} onChange={e => upd('fecha_fin', e.target.value)} />
                  </div>
                </div>
              </div>
              {w.presupuesto_diario && w.fecha_inicio && w.fecha_fin && (
                <div style={{ ...S.alert('info'), marginTop: '8px' }}>
                  Presupuesto total estimado: {formatMoney(
                    w.presupuesto_diario * Math.ceil((new Date(w.fecha_fin) - new Date(w.fecha_inicio)) / 86400000)
                  )} ({Math.ceil((new Date(w.fecha_fin) - new Date(w.fecha_inicio)) / 86400000)} días)
                </div>
              )}
            </>
          )}

          {wizardStep === 2 && (
            <>
              <div style={S.fieldGroup}>
                <label style={S.label}>Zona de cobertura</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(ZONA_LABELS).map(([val, lab]) => (
                    <button key={val}
                      style={{
                        ...S.btnSecondary,
                        border: w.zona === val ? '2px solid #3b82f6' : '1px solid #3a3a45',
                        background: w.zona === val ? '#3b82f622' : '#2a2a35'
                      }}
                      onClick={() => upd('zona', val)}>
                      {w.zona === val ? '◉ ' : '○ '}{lab}
                    </button>
                  ))}
                </div>
              </div>
              <div style={S.row}>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Edad mínima: {w.edad_min}</label>
                    <input type="range" min="18" max="65" value={w.edad_min}
                      onChange={e => upd('edad_min', parseInt(e.target.value))}
                      style={{ width: '100%' }} />
                  </div>
                </div>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Edad máxima: {w.edad_max}</label>
                    <input type="range" min="18" max="65" value={w.edad_max}
                      onChange={e => upd('edad_max', parseInt(e.target.value))}
                      style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Género</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[['todos', 'Todos'], ['hombres', 'Hombres'], ['mujeres', 'Mujeres']].map(([val, lab]) => (
                    <button key={val}
                      style={{
                        ...S.btnSecondary,
                        border: w.genero === val ? '2px solid #3b82f6' : '1px solid #3a3a45',
                        background: w.genero === val ? '#3b82f622' : '#2a2a35'
                      }}
                      onClick={() => upd('genero', val)}>
                      {lab}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {wizardStep === 3 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <label style={{ ...S.label, margin: 0 }}>Seleccionar creativo</label>
                <UploadButton onUpload={subirCreativo} />
              </div>
              {creativos.length === 0 ? (
                <div style={S.empty}>
                  <div>No hay creativos. Subí una imagen para empezar.</div>
                </div>
              ) : (
                <div style={S.grid}>
                  {creativos.map(c => (
                    <div key={c.id} style={S.imgCard(w.creativo_id === c.id)}
                      onClick={() => upd('creativo_id', c.id)}>
                      {c.url_preview ? (
                        <img src={c.url_preview} alt={c.nombre} style={S.imgThumb}
                          onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div style={{ ...S.imgThumb, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#12121a', color: '#8a8880' }}>
                          🖼
                        </div>
                      )}
                      <div style={S.imgInfo}>
                        <div style={{ color: '#e2e0d8', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.nombre}
                        </div>
                        <div>{c.ratio} · {c.ancho}x{c.alto}</div>
                      </div>
                      {w.creativo_id === c.id && (
                        <div style={{ position: 'absolute', top: '6px', right: '6px', background: '#3b82f6', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                          ✓
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {wizardStep === 4 && (
            <>
              <div style={S.fieldGroup}>
                <label style={S.label}>Texto principal (máx 125 caracteres)</label>
                <textarea style={S.textarea}
                  placeholder="Ej: Internet Fibra Óptica REAL. Hasta 600 Mb simétricos en tu casa."
                  value={w.texto_principal}
                  onChange={e => upd('texto_principal', e.target.value)}
                  maxLength={200} />
                <div style={S.charCount(w.texto_principal.length, 125)}>
                  {w.texto_principal.length}/125
                </div>
              </div>
              <div style={S.row}>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Título / Headline (máx 40 chars)</label>
                    <input style={S.input}
                      placeholder="Ej: WeConnect Fibra desde $XX.XXX/mes"
                      value={w.titulo}
                      onChange={e => upd('titulo', e.target.value)}
                      maxLength={60} />
                    <div style={S.charCount(w.titulo.length, 40)}>
                      {w.titulo.length}/40
                    </div>
                  </div>
                </div>
                <div style={S.col}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Descripción (máx 30 chars)</label>
                    <input style={S.input}
                      placeholder="Ej: Instalación sin cargo"
                      value={w.descripcion}
                      onChange={e => upd('descripcion', e.target.value)}
                      maxLength={50} />
                    <div style={S.charCount(w.descripcion.length, 30)}>
                      {w.descripcion.length}/30
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ ...S.alert('info') }}>
                {w.tipo === 'ctwa'
                  ? 'Botón del anuncio: "Enviar mensaje por WhatsApp" → abre WhatsApp al 7064'
                  : 'Botón del anuncio: "Más información" → lleva a weconnect.ar'}
              </div>
            </>
          )}

          {wizardStep === 5 && (
            <>
              <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>📋 Resumen de campaña</div>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '14px' }}>
                <span style={{ color: '#8a8880' }}>Tipo:</span>
                <span>{TIPO_LABELS[w.tipo]}</span>
                <span style={{ color: '#8a8880' }}>Nombre:</span>
                <span style={{ fontWeight: '600' }}>{w.nombre}</span>
                <span style={{ color: '#8a8880' }}>Presupuesto:</span>
                <span>{formatMoney(w.presupuesto_diario)}/día</span>
                <span style={{ color: '#8a8880' }}>Período:</span>
                <span>{w.fecha_inicio} → {w.fecha_fin}</span>
                <span style={{ color: '#8a8880' }}>Zona:</span>
                <span>{ZONA_LABELS[w.zona]}</span>
                <span style={{ color: '#8a8880' }}>Público:</span>
                <span>{w.edad_min}-{w.edad_max} años, {w.genero}</span>
                <span style={{ color: '#8a8880' }}>Creativo:</span>
                <span>{creativos.find(c => c.id === w.creativo_id)?.nombre || '—'}</span>
                <span style={{ color: '#8a8880' }}>Texto:</span>
                <span>{w.texto_principal || '—'}</span>
                <span style={{ color: '#8a8880' }}>Título:</span>
                <span>{w.titulo || '—'}</span>
              </div>
              <div style={{ ...S.alert('info'), marginTop: '16px' }}>
                Se creará como <strong>borrador</strong>. Después podés publicarla en Meta desde el detalle.
              </div>
            </>
          )}
        </div>

        {/* Nav buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <button style={S.btnSecondary}
            onClick={() => wizardStep > 1 ? setWizardStep(wizardStep - 1) : setVista('dashboard')}>
            {wizardStep > 1 ? '← Anterior' : '← Cancelar'}
          </button>
          {wizardStep < 5 ? (
            <button style={S.btnPrimary} onClick={() => setWizardStep(wizardStep + 1)}>
              Siguiente →
            </button>
          ) : (
            <button style={S.btnSuccess} onClick={crearBorrador} disabled={loading}>
              {loading ? 'Creando...' : '📤 Crear borrador'}
            </button>
          )}
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DETALLE CAMPAÑA
  // ═══════════════════════════════════════════════════════════════════════════

  function renderDetail() {
    const c = campanaSeleccionada;
    if (!c) return <div style={S.empty}>Cargando...</div>;

    return (
      <>
        <div style={S.header}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={S.link} onClick={() => { setVista('dashboard'); cargarResumen(); }}>← Volver</span>
            </div>
            <h2 style={{ ...S.title, marginTop: '8px' }}>{c.nombre}</h2>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
              <span style={S.badge(ESTADO_COLORS[c.estado])}>{c.estado}</span>
              <span style={{ fontSize: '13px', color: '#8a8880' }}>{TIPO_LABELS[c.tipo]}</span>
              <span style={{ fontSize: '13px', color: '#8a8880' }}>· {ZONA_LABELS[c.zona]}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {c.estado === 'borrador' && (
              <button style={S.btnSuccess} onClick={() => publicarCampana(c.id)} disabled={loading}>
                {loading ? 'Publicando...' : '📤 Publicar en Meta'}
              </button>
            )}
            {c.estado === 'activa' && (
              <button style={{ ...S.btnSecondary, borderColor: '#eab308' }} onClick={() => pausarCampana(c.id)}>
                ⏸ Pausar
              </button>
            )}
            {c.estado === 'pausada' && c.meta_campaign_id && (
              <button style={S.btnSuccess} onClick={() => reanudarCampana(c.id)}>
                ▶ Activar
              </button>
            )}
            {c.meta_campaign_id && (
              <button style={S.btnSecondary} onClick={() => syncCampana(c.id)}>
                ↻ Sync métricas
              </button>
            )}
            {c.estado !== 'finalizada' && (
              <button style={S.btnDanger} onClick={() => eliminarCampana(c.id)}>
                🗑
              </button>
            )}
          </div>
        </div>

        {c.error_meta && (
          <div style={S.alert('error')}>Error Meta: {c.error_meta}</div>
        )}

        {/* KPIs campaña */}
        <div style={S.kpiRow}>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatMoney(c.gasto_total)}</div>
            <div style={S.kpiLabel}>Gasto total</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatNumber(c.impresiones)}</div>
            <div style={S.kpiLabel}>Impresiones</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatNumber(c.clics)}</div>
            <div style={S.kpiLabel}>Clics</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{c.tipo === 'ctwa' ? formatNumber(c.conversaciones) : formatNumber(c.leads)}</div>
            <div style={S.kpiLabel}>{c.tipo === 'ctwa' ? 'Conversaciones' : 'Leads'}</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{c.ctr ? c.ctr.toFixed(2) + '%' : '—'}</div>
            <div style={S.kpiLabel}>CTR</div>
          </div>
          <div style={S.kpi}>
            <div style={S.kpiValue}>{formatMoney(c.cpl)}</div>
            <div style={S.kpiLabel}>Costo / resultado</div>
          </div>
        </div>

        {/* Grafico simple de gasto diario */}
        {metricas.length > 0 && (
          <div style={S.card}>
            <div style={{ fontWeight: '600', marginBottom: '12px' }}>Gasto diario (ultimos 30 dias)</div>
            {(() => {
              const maxGasto = Math.max(...metricas.map(m => parseFloat(m.gasto) || 0), 1);
              const esUnSoloDia = metricas.length === 1;

              return (
                <>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '2px',
                    height: '100px',
                    justifyContent: esUnSoloDia ? 'flex-start' : 'normal'
                  }}>
                    {metricas.map((m, i) => {
                      const gasto = parseFloat(m.gasto) || 0;
                      const pct = gasto > 0 ? Math.max((gasto / maxGasto) * 100, 4) : 0;

                      return (
                        <div
                          key={i}
                          title={m.fecha + ': ' + formatMoney(gasto)}
                          style={{
                            flex: esUnSoloDia ? '0 0 auto' : 1,
                            width: esUnSoloDia ? '24px' : undefined,
                            minWidth: '4px',
                            height: pct + '%',
                            background: '#3b82f6',
                            borderRadius: '2px 2px 0 0',
                            transition: 'height 0.3s'
                          }}
                        />
                      );
                    })}
                  </div>

                  {esUnSoloDia && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#8a8880' }}>
                      Primer dia con metricas. El historial se completa automaticamente con los proximos dias.
                    </div>
                  )}
                </>
              );
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', color: '#8a8880' }}>
              <span>{metricas[0]?.fecha}</span>
              <span>{metricas[metricas.length - 1]?.fecha}</span>
            </div>
          </div>
        )}

        {/* Info de la campaña */}
        <div style={S.card}>
          <div style={{ fontWeight: '600', marginBottom: '12px' }}>Configuración</div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '6px', fontSize: '13px' }}>
            <span style={{ color: '#8a8880' }}>Presupuesto diario:</span>
            <span>{formatMoney(c.presupuesto_diario)}</span>
            <span style={{ color: '#8a8880' }}>Período:</span>
            <span>{c.fecha_inicio ? new Date(c.fecha_inicio).toLocaleDateString('es-AR') : '—'} → {c.fecha_fin ? new Date(c.fecha_fin).toLocaleDateString('es-AR') : '—'}</span>
            <span style={{ color: '#8a8880' }}>Público:</span>
            <span>{c.edad_min}-{c.edad_max} años, {c.genero}</span>
            <span style={{ color: '#8a8880' }}>Texto principal:</span>
            <span>{c.texto_principal || '—'}</span>
            <span style={{ color: '#8a8880' }}>Título:</span>
            <span>{c.titulo || '—'}</span>
            {c.meta_campaign_id && (
              <>
                <span style={{ color: '#8a8880' }}>Meta Campaign ID:</span>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{c.meta_campaign_id}</span>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BIBLIOTECA CREATIVOS
  // ═══════════════════════════════════════════════════════════════════════════

  function renderLibrary() {
    return (
      <>
        <div style={S.header}>
          <div>
            <span style={S.link} onClick={() => { setVista('dashboard'); cargarResumen(); }}>← Volver</span>
            <h2 style={{ ...S.title, marginTop: '8px' }}>🖼 Biblioteca de creativos</h2>
            <div style={S.subtitle}>Imágenes para campañas · JPG o PNG · Mínimo 600x600px</div>
          </div>
          <UploadButton onUpload={subirCreativo} />
        </div>

        {/* Tamaños recomendados */}
        <div style={{ ...S.alert('info'), marginBottom: '16px' }}>
          <strong>Tamaños recomendados:</strong> Feed: 1080×1080 (1:1) o 1080×1350 (4:5) · Stories/Reels: 1080×1920 (9:16) · Landscape: 1200×628
        </div>

        {creativos.length === 0 ? (
          <div style={S.empty}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🖼</div>
            <div>No hay creativos todavía. Subí una imagen para empezar.</div>
          </div>
        ) : (
          <div style={{ ...S.grid, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
            {creativos.map(c => (
              <div key={c.id} style={S.imgCard(false)}>
                {c.url_preview ? (
                  <img src={c.url_preview} alt={c.nombre} style={{ ...S.imgThumb, height: '160px' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ ...S.imgThumb, height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#12121a', color: '#8a8880', fontSize: '32px' }}>
                    🖼
                  </div>
                )}
                <div style={{ ...S.imgInfo, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#e2e0d8', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                      {c.nombre}
                    </div>
                    <div>{c.ratio} · {c.ancho}x{c.alto} · {Math.round((c.tamano_bytes || 0) / 1024)}KB</div>
                    <div>Usado en {c.usado_en || 0} campaña(s)</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); eliminarCreativo(c.id); }}
                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
                    title="Eliminar">
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Botón de upload
// ═══════════════════════════════════════════════════════════════════════════════

function UploadButton({ onUpload }) {
  const inputRef = useRef(null);

  return (
    <>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png"
        style={{ display: 'none' }}
        onChange={e => {
          if (e.target.files[0]) {
            onUpload(e.target.files[0]);
            e.target.value = '';
          }
        }} />
      <button style={{
        background: '#2a2a35', color: '#e2e0d8', border: '1px solid #3a3a45',
        borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px'
      }} onClick={() => inputRef.current?.click()}>
        📤 Subir imagen
      </button>
    </>
  );
}
